import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { FaTasks, FaPlus, FaCheck, FaExclamationCircle } from 'react-icons/fa';
import { toast } from 'react-toastify';
import CrmTaskService, { CrmTask } from '../services/CrmTaskService';
import CrmTaskModal from './CrmTaskModal';
import QuickTaskInput from './QuickTaskInput';

interface OpenTasksPanelProps {
  partnerId?: number;
  partnerName?: string;
  maxItems?: number;
  showViewAll?: boolean;
  title?: string;
}

export default function OpenTasksPanel({
  partnerId,
  partnerName,
  maxItems = 5,
  showViewAll = true,
  title = 'Open Tasks',
}: OpenTasksPanelProps): React.JSX.Element {
  const navigate = useNavigate();
  const [tasks, setTasks] = useState<CrmTask[]>([]);
  const [loading, setLoading] = useState(true);
  const [showTaskModal, setShowTaskModal] = useState(false);
  const [editingTask, setEditingTask] = useState<CrmTask | null>(null);
  const [completingTaskId, setCompletingTaskId] = useState<number | null>(null);

  const loadTasks = useCallback(async () => {
    setLoading(true);
    try {
      const today = new Date();
      const from = new Date(today);
      from.setDate(from.getDate() - 7);
      const to = new Date(today);
      to.setDate(to.getDate() + 14);

      const params: Parameters<typeof CrmTaskService.list>[0] = {
        status: 'OPEN,IN_PROGRESS',
        due_from: from.toISOString().split('T')[0],
        due_to: to.toISOString().split('T')[0],
      };

      if (partnerId) {
        params.partner_id = partnerId;
      }

      const data = await CrmTaskService.list(params);

      const sorted = data.sort((a, b) => {
        const aOverdue = CrmTaskService.isOverdue(a.due_date);
        const bOverdue = CrmTaskService.isOverdue(b.due_date);

        if (aOverdue && !bOverdue) return -1;
        if (!aOverdue && bOverdue) return 1;

        if (!a.due_date && !b.due_date) return 0;
        if (!a.due_date) return 1;
        if (!b.due_date) return -1;

        return new Date(a.due_date).getTime() - new Date(b.due_date).getTime();
      });

      setTasks(sorted);
    } catch (err) {
      console.error('Failed to load tasks:', err);
    } finally {
      setLoading(false);
    }
  }, [partnerId]);

  useEffect(() => {
    loadTasks();
  }, [loadTasks]);

  const handleCompleteTask = async (task: CrmTask, e: React.MouseEvent) => {
    e.stopPropagation();
    setCompletingTaskId(task.id);
    try {
      await CrmTaskService.changeStatus(task.id, 'DONE');
      toast.success('Task completed');
      loadTasks();
    } catch (err) {
      console.error('Failed to complete task:', err);
      toast.error('Failed to complete task');
    } finally {
      setCompletingTaskId(null);
    }
  };

  const handleTaskClick = (task: CrmTask) => {
    if (task.offer) {
      navigate(`/offers/${task.offer}`);
    } else if (task.invoice) {
      navigate(`/invoices/${task.invoice}`);
    } else if (task.project) {
      navigate(`/projects/${task.project}`);
    } else if (task.partner) {
      navigate(`/contacts/${task.partner}`);
    } else {
      setEditingTask(task);
      setShowTaskModal(true);
    }
  };

  const handleNewTask = () => {
    setEditingTask(null);
    setShowTaskModal(true);
  };

  const handleTaskSaved = () => {
    setShowTaskModal(false);
    setEditingTask(null);
    loadTasks();
  };

  const handleQuickTaskCreated = (newTask: CrmTask) => {
    loadTasks();
  };

  const displayTasks = tasks.slice(0, maxItems);
  const hasMore = tasks.length > maxItems;

  return (
    <div
      style={{
        background: 'white',
        borderRadius: '12px',
        boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
        overflow: 'hidden',
      }}
    >
      {/* Header */}
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          padding: '16px 20px',
          borderBottom: '1px solid #e0e3ef',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          <FaTasks size={16} color="#7c3aed" />
          <h3 style={{ margin: 0, fontSize: '15px', fontWeight: 600, color: '#021a4a' }}>
            {title}
          </h3>
          {tasks.length > 0 && (
            <span
              style={{
                padding: '2px 8px',
                borderRadius: '10px',
                fontSize: '12px',
                fontWeight: 600,
                background: '#f3f4f6',
                color: '#374151',
              }}
            >
              {tasks.length}
            </span>
          )}
        </div>
        <button
          onClick={handleNewTask}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '6px',
            padding: '6px 12px',
            border: '1px solid #7c3aed',
            borderRadius: '6px',
            background: 'white',
            color: '#7c3aed',
            fontSize: '13px',
            fontWeight: 600,
            cursor: 'pointer',
          }}
        >
          <FaPlus size={10} />
          New task
        </button>
      </div>

      {/* Quick Task Input */}
      <QuickTaskInput
        partnerId={partnerId}
        partnerName={partnerName}
        placeholder={partnerId ? 'Add task for this customer...' : 'Add task...'}
        onTaskCreated={handleQuickTaskCreated}
      />

      {/* Content */}
      <div style={{ padding: '8px 0' }}>
        {loading ? (
          <div style={{ padding: '24px', textAlign: 'center', color: '#888' }}>
            Loading...
          </div>
        ) : displayTasks.length === 0 ? (
          <div style={{ padding: '32px 20px', textAlign: 'center', color: '#888' }}>
            <FaTasks size={24} style={{ marginBottom: '8px', opacity: 0.4 }} />
            <p style={{ margin: 0, fontSize: '14px' }}>No open tasks</p>
          </div>
        ) : (
          <>
            {displayTasks.map((task) => {
              const isOverdue = CrmTaskService.isOverdue(task.due_date);
              const isToday = CrmTaskService.isDueToday(task.due_date);
              const isCompleting = completingTaskId === task.id;

              return (
                <div
                  key={task.id}
                  onClick={() => handleTaskClick(task)}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '12px',
                    padding: '12px 20px',
                    cursor: 'pointer',
                    background: isOverdue ? '#fef2f2' : 'transparent',
                    borderLeft: isOverdue ? '3px solid #dc2626' : '3px solid transparent',
                    transition: 'background 0.15s',
                  }}
                  onMouseEnter={(e) => {
                    if (!isOverdue) e.currentTarget.style.background = '#f8f9fc';
                  }}
                  onMouseLeave={(e) => {
                    if (!isOverdue) e.currentTarget.style.background = 'transparent';
                  }}
                >
                  {/* Checkbox */}
                  <button
                    onClick={(e) => handleCompleteTask(task, e)}
                    disabled={isCompleting}
                    style={{
                      width: '22px',
                      height: '22px',
                      borderRadius: '50%',
                      border: `2px solid ${isOverdue ? '#dc2626' : '#d1d5db'}`,
                      background: 'white',
                      cursor: isCompleting ? 'wait' : 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexShrink: 0,
                      transition: 'all 0.15s',
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.background = '#d1fae5';
                      e.currentTarget.style.borderColor = '#059669';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.background = 'white';
                      e.currentTarget.style.borderColor = isOverdue ? '#dc2626' : '#d1d5db';
                    }}
                    title="Mark as done"
                  >
                    <FaCheck size={10} color="#059669" style={{ opacity: 0 }} />
                  </button>

                  {/* Due date */}
                  <div
                    style={{
                      width: '60px',
                      flexShrink: 0,
                      fontSize: '13px',
                      fontWeight: 600,
                      color: isOverdue ? '#dc2626' : isToday ? '#7c3aed' : '#666',
                    }}
                  >
                    {isOverdue && <FaExclamationCircle size={10} style={{ marginRight: '4px' }} />}
                    {CrmTaskService.formatDueDate(task.due_date)}
                  </div>

                  {/* Partner name */}
                  {!partnerId && task.partner_name && (
                    <div
                      style={{
                        width: '140px',
                        flexShrink: 0,
                        fontSize: '13px',
                        color: '#666',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {task.partner_name}
                    </div>
                  )}

                  {/* Task title */}
                  <div
                    style={{
                      flex: 1,
                      fontSize: '14px',
                      fontWeight: 600,
                      color: '#021a4a',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {task.title}
                  </div>

                  {/* Priority badge */}
                  {task.priority === 'HIGH' && (
                    <span
                      style={{
                        padding: '2px 6px',
                        borderRadius: '4px',
                        fontSize: '10px',
                        fontWeight: 600,
                        background: '#fee2e2',
                        color: '#991b1b',
                      }}
                    >
                      HIGH
                    </span>
                  )}
                </div>
              );
            })}
          </>
        )}
      </div>

      {/* Footer */}
      {showViewAll && hasMore && (
        <div
          style={{
            padding: '12px 20px',
            borderTop: '1px solid #e0e3ef',
            textAlign: 'center',
          }}
        >
          <button
            onClick={() => {
              if (partnerId) {
                navigate(`/contacts/${partnerId}?tab=tasks`);
              } else {
                navigate('/sales?tab=tasks');
              }
            }}
            style={{
              background: 'none',
              border: 'none',
              color: '#7c3aed',
              fontSize: '13px',
              fontWeight: 600,
              cursor: 'pointer',
            }}
          >
            View all {tasks.length} tasks
          </button>
        </div>
      )}

      {/* Task Modal */}
      <CrmTaskModal
        isOpen={showTaskModal}
        task={editingTask}
        partnerId={partnerId}
        partnerName={partnerName}
        onClose={() => {
          setShowTaskModal(false);
          setEditingTask(null);
        }}
        onSaved={handleTaskSaved}
      />
    </div>
  );
}
